import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { splashText } from './loksy-landing/mitraSplash';
import './loksy-landing/loksyLanding.css';
import './loksy-landing/loksyMitraStory.css';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'hi', label: 'हिंदी' },
];

const SLIDE_MS = 4800;

function SplashCard({ image, title, body, hero, imageLeft, brandMark }) {
  return (
    <article
      className={`mitra-split-card${hero ? ' is-hero' : ''}${imageLeft ? ' image-left' : ' text-first'}`}
    >
      <div className="mitra-split-photo">
        <div className={hero ? 'mitra-hero-photo' : 'mitra-white-photo'}>
          <img src={image} alt="" />
        </div>
      </div>
      <div className="mitra-split-copy">
        <h1 className={hero ? 'mitra-hero-title' : 'mitra-white-title'}>{title}</h1>
        {hero ? (
          <div className="mitra-hero-brand">
            <img src={brandMark} alt="" />
            <span>{body}</span>
          </div>
        ) : (
          <p className="mitra-white-body">{body}</p>
        )}
      </div>
    </article>
  );
}

export default function SplashStory({
  screens,
  playStore,
  loginTo,
  brandMark,
  ui,
  documentTitle,
}) {
  const [lang, setLang] = useState('te');
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const touchX = useRef(null);
  const total = screens.length;
  const screen = screens[index];
  const last = index === total - 1;
  const copy = splashText(screen, lang);
  const autoplay = playing;

  const go = useCallback(
    (next) => {
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  useEffect(() => {
    document.body.classList.add('loksy-landing-active', 'mitra-story-page');
    const previous = document.title;
    document.title = documentTitle;
    return () => {
      document.body.classList.remove('loksy-landing-active', 'mitra-story-page');
      document.title = previous;
    };
  }, [documentTitle]);

  useEffect(() => {
    if (!autoplay) return undefined;
    const timer = window.setTimeout(() => go(index + 1), SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [autoplay, go, index]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'ArrowRight') go(index + 1);
      if (event.key === 'ArrowLeft') go(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, index]);

  return (
    <div className={`loksy-landing mitra-story lang-${lang}`}>
      <header className="loksy-nav mitra-story-nav">
        <Link to="/" className="loksy-brand mitra-story-brand">
          Loksy
          <span />
        </Link>
        <div className="mitra-lang-row">
          {LANGS.map((item) => (
            <button
              key={item.code}
              type="button"
              className={`mitra-lang-chip${lang === item.code ? ' on' : ''}`}
              onClick={() => setLang(item.code)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div
          className={`mitra-progress${autoplay ? ' on' : ''}`}
          key={autoplay ? index : 'paused'}
          style={autoplay ? { animationDuration: `${SLIDE_MS}ms` } : undefined}
        />
      </header>

      <section
        className="mitra-splash-stage"
        onTouchStart={(event) => {
          touchX.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          const start = touchX.current;
          const end = event.changedTouches[0]?.clientX;
          touchX.current = null;
          if (start == null || end == null) return;
          const delta = end - start;
          if (delta < -40) go(index + 1);
          if (delta > 40) go(index - 1);
        }}
      >
        <div className="mitra-splash-top">
          <p className="loksy-kicker">{ui.pageKicker[lang]}</p>
          <Link to={loginTo} className="mitra-skip">
            {ui.skip[lang]}
          </Link>
        </div>

        <div key={`${screen.id}-${lang}`} className="mitra-phone-slide">
          <SplashCard
            image={screen.image}
            title={copy.title}
            body={copy.body}
            hero={Boolean(screen.hero)}
            imageLeft={index % 2 === 1}
            brandMark={brandMark}
          />
        </div>

        <div className="mitra-dots">
          {screens.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`${i + 1} / ${total}`}
              className={i === index ? 'on' : ''}
              onClick={() => go(i)}
            />
          ))}
        </div>

        <div className="mitra-nav-row">
          <button type="button" className="mitra-prev" onClick={() => go(index - 1)}>
            {ui.prev[lang]}
          </button>
          {last ? (
            <Link className="mitra-next" to={loginTo}>
              {ui.continue[lang]}
            </Link>
          ) : (
            <button type="button" className="mitra-next" onClick={() => go(index + 1)}>
              {ui.next[lang]}
            </button>
          )}
        </div>

        <div className="mitra-phone-controls">
          <button type="button" onClick={() => setPlaying((value) => !value)}>
            {playing ? ui.pause[lang] : ui.autoplay[lang]}
          </button>
          <span>
            {index + 1} / {total}
          </span>
        </div>

        <div className="mitra-cta-row">
          <a
            className="loksy-btn loksy-btn-primary"
            href={playStore}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ui.playCta[lang]}
          </a>
          <Link className="loksy-btn loksy-btn-ghost" to={loginTo}>
            {ui.webCta[lang]}
          </Link>
          <a href={playStore} target="_blank" rel="noopener noreferrer">
            <img
              className="mitra-play-badge"
              src="/images/google-play-badge.png"
              alt="Get it on Google Play"
            />
          </a>
        </div>
      </section>
    </div>
  );
}
